import React, { useEffect, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import Request from '../../../helpers/request'
import * as url from '../../../helpers/url_helper'

// Format an ISO timestamp in IST for operator display (logs/DB are UTC).
const formatIST = (iso: string | null): string => {
    if (!iso) return 'indefinite'
    try {
        return new Date(iso).toLocaleString('en-IN', {
            dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kolkata',
        }) + ' IST'
    } catch { return iso }
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children }: { children: React.ReactNode }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC' }}>
        {children}
    </td>
)

const Polling = () => {
    const [polling, setPolling] = useState<any>(null)
    const [loaded, setLoaded] = useState(false)
    const [busy, setBusy] = useState(false)

    const fetchPolling = () => {
        Request.post(url.POLLING_STATUS, {})
            .then((res: any) => setPolling(res?.data?.data || null))
            .catch(() => setPolling(null))
            .finally(() => setLoaded(true))
    }

    useEffect(() => { fetchPolling() }, [])

    const toggleGlobal = () => {
        if (!polling) return
        setBusy(true)
        Request.post(url.POLLING_GLOBAL, { enabled: !polling.globalEnabled })
            .then((res: any) => {
                setPolling(res?.data?.data || null)
                toast.success('Global polling updated')
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to update global polling'))
            .finally(() => setBusy(false))
    }

    const resumePlace = (placeId: string) => {
        setBusy(true)
        Request.post(url.POLLING_PLACE, { placeId, enabled: true })
            .then((res: any) => {
                setPolling(res?.data?.data || null)
                toast.success('Network polling resumed')
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to resume network'))
            .finally(() => setBusy(false))
    }

    const stopAllow = (placeId: string) => {
        setBusy(true)
        Request.post(url.POLLING_PLACE_ALLOW, { placeId, enabled: false })
            .then((res: any) => {
                setPolling(res?.data?.data || null)
                toast.success('Test polling stopped for network')
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to stop test polling'))
            .finally(() => setBusy(false))
    }

    const disabledPlaces: any[] = polling?.disabledPlaces || []
    const allowedPlaces: any[] = polling?.allowedPlaces || []
    const globalEnabled: boolean = !!(polling && polling.globalEnabled)

    // Dual-list conflict: when a place sits in both arrays, only the one
    // matching the master switch state applies. Surface so the operator can
    // clean up the inactive entry.
    const conflictIds: string[] = polling
        ? disabledPlaces
            .map((p: any) => p.placeId)
            .filter((id: string) => allowedPlaces.some((a: any) => a.placeId === id))
        : []

    return (
        <div className="page-content" style={{ padding: 30 }}>
            <ToastContainer />
            <h4 style={{ marginBottom: 24, fontWeight: 600 }}>Polling Control</h4>

            {conflictIds.length > 0 && (
                <div
                    data-testid="polling-conflict-banner"
                    style={{ background: '#FFFAEB', border: '1px solid #FEDF89', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}
                >
                    <strong style={{ color: '#B54708' }}>Conflicting polling entries</strong>
                    <p style={{ margin: '4px 0 8px', fontSize: 13, color: '#B54708' }}>
                        These networks appear on both the pause and allow lists. The master switch decides which applies — clean up by removing them from the inactive list.
                    </p>
                    <div style={{ fontSize: 12, color: '#B54708', wordBreak: 'break-all' }}>
                        {conflictIds.map(id => (
                            <code key={id} style={{ display: 'inline-block', marginRight: 8 }}>{id}</code>
                        ))}
                    </div>
                </div>
            )}

            {/* Global master switch */}
            <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, padding: '20px 24px', marginBottom: 20 }}>
                <h6 style={{ margin: '0 0 6px', fontWeight: 600 }}>Global polling</h6>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: '#858D9D' }}>
                    Master switch for backend status-polling across every network. When paused, the backend stops actively polling devices for state — useful while debugging firmware or a specific network. Use the allow-list below to keep polling one friendly network while everyone else is paused.
                </p>
                {!loaded ? (
                    <span style={{ color: '#858D9D' }}>Loading…</span>
                ) : !polling ? (
                    <span style={{ color: '#858D9D' }}>Polling status unavailable</span>
                ) : (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span data-testid="global-state" style={{ fontSize: 16, color: globalEnabled ? '#16A34A' : '#D92D20' }}>
                            ● {globalEnabled ? 'Active' : 'Paused globally'}
                        </span>
                        <button
                            data-testid="global-toggle"
                            onClick={toggleGlobal}
                            disabled={busy}
                            style={{
                                padding: '8px 18px', borderRadius: 6,
                                border: `1px solid ${globalEnabled ? '#D92D20' : '#16A34A'}`,
                                background: '#fff',
                                color: globalEnabled ? '#D92D20' : '#16A34A',
                                cursor: busy ? 'not-allowed' : 'pointer',
                                fontSize: 14,
                            }}
                        >
                            {globalEnabled ? 'Pause all polling' : 'Resume polling'}
                        </button>
                    </div>
                )}
            </div>

            {/* Master ON → show paused networks. Master OFF → show allowed networks. */}
            {polling && globalEnabled && (
                <div data-testid="paused-section" style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #E4E7EC' }}>
                        <h6 style={{ margin: 0, fontWeight: 600 }}>Paused networks</h6>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#858D9D' }}>
                            Networks (places) with their polling currently paused. Per-network pause can also be set from the place's Network Management tab.
                        </p>
                    </div>
                    {disabledPlaces.length === 0 ? (
                        <div style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                            No paused networks
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <TH>Place ID</TH>
                                    <TH>Auto-resumes</TH>
                                    <TH>Action</TH>
                                </tr>
                            </thead>
                            <tbody>
                                {disabledPlaces.map((p: any) => (
                                    <tr key={p.placeId}>
                                        <TD><code style={{ fontSize: 12 }}>{p.placeId}</code></TD>
                                        <TD>{formatIST(p.expiresAt)}</TD>
                                        <TD>
                                            <button
                                                data-testid={`resume-${p.placeId}`}
                                                onClick={() => resumePlace(p.placeId)}
                                                disabled={busy}
                                                style={{ padding: '4px 12px', fontSize: 12, border: '1px solid #16A34A', background: '#fff', color: '#16A34A', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer' }}
                                            >
                                                Resume
                                            </button>
                                        </TD>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {polling && !globalEnabled && (
                <div data-testid="allowed-section" style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #E4E7EC' }}>
                        <h6 style={{ margin: 0, fontWeight: 600 }}>Allowed networks (test mode)</h6>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#858D9D' }}>
                            With global polling paused, only the networks below are being actively polled. Add a network from its Network Management tab to test polling on it.
                        </p>
                    </div>
                    {allowedPlaces.length === 0 ? (
                        <div style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                            No networks are being polled
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <TH>Place ID</TH>
                                    <TH>Auto-clears</TH>
                                    <TH>Action</TH>
                                </tr>
                            </thead>
                            <tbody>
                                {allowedPlaces.map((p: any) => (
                                    <tr key={p.placeId}>
                                        <TD><code style={{ fontSize: 12 }}>{p.placeId}</code></TD>
                                        <TD>{formatIST(p.expiresAt)}</TD>
                                        <TD>
                                            <button
                                                data-testid={`stop-allow-${p.placeId}`}
                                                onClick={() => stopAllow(p.placeId)}
                                                disabled={busy}
                                                style={{ padding: '4px 12px', fontSize: 12, border: '1px solid #D92D20', background: '#fff', color: '#D92D20', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer' }}
                                            >
                                                Stop test polling
                                            </button>
                                        </TD>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    )
}

export default Polling
